import { useState, useEffect, useCallback } from "react";
import { TrendingUp, Info, Settings } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useToast } from "../../components/ui/toast";
import { setCompleted } from "../../lib/uwProgress";
import { formatCurrency } from "../../lib/utils";
import { Deal } from "../../types/deal";
import { RampSettings } from "../../types/ramp";
import {
  revenueRampPresets,
  costRampPresets,
  createDefaultRampSettings,
} from "../../data/rampDefaults";
import RampCard from "../../components/RampCard";
import LabelCheck from "../../components/LabelCheck";
import { debounceNode } from "@/utils/useDebounce";
import { useProjectSave } from "../../lib/persist/ProjectSaveContext";
import {
  calculateAllRampMetrics,
  calculateAnnualDepreciation,
  calculateIndexes,
  getCurrentPreset,
  getSettingsSummary,
  hasChanges,
} from "@/lib/calculateRampMacro";
import { saveProject } from "@/lib/datasource";

type TemplateModeProps = {
  kpis?: any;
  onChange?: (next: any) => void;
  hideActions?: boolean;
};

type ProjectModeProps = {
  deal: any;
  onDealChange: (updatedDeal: Deal) => void;
  onSaved?: () => void;
};

type RampMacroSettingsProps = ProjectModeProps & TemplateModeProps;

type RampPresetKey = "conservative" | "standard" | "ambitious";

function QuickButton({
  value,
  active,
  onClick,
}: {
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded text-sm font-medium transition-all ${
        active
          ? "bg-primary-blue text-white shadow-sm"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {value}%
    </button>
  );
}

export default function RampMacroSettings(props: RampMacroSettingsProps) {
  const { deal, onDealChange, onSaved, kpis, onChange, hideActions } = props;
  const isTemplate = !!kpis && typeof onChange === "function";

  if (isTemplate) {
    return null;
  }

  const { toast } = useToast();

  const [rampSettings, setRampSettings] = useState<RampSettings | null>(null);
  const [originalSettings, setOriginalSettings] = useState<RampSettings | null>(
    null,
  );
  const [saveState, setSaveState] = useState<"idle" | "saving" | "success">(
    "idle",
  );
  const [animatedFields, setAnimatedFields] = useState<Set<string>>(new Set());
  const [calculatedMetrics, setCalculatedMetrics] = useState<any>(null);

  const loadRampData = useCallback(() => {
    if (!deal) return;

    const existingRampSettings = deal.kpis?.rampSettings;
    const existingRamp = deal.kpis?.ramp;

    let settings = createDefaultRampSettings();

    if (existingRampSettings) {
      settings = {
        ...settings,
        ...existingRampSettings,
        applyCostRamp: {
          ...settings.applyCostRamp,
          ...(existingRampSettings.applyCostRamp || {}),
          payroll: existingRampSettings.applyCostRamp?.payroll ?? true,
          otherOpex: existingRampSettings.applyCostRamp?.otherOpex ?? true,
          departmental:
            existingRampSettings.applyCostRamp?.departmental ?? true,
          undistributed:
            existingRampSettings.applyCostRamp?.undistributed ?? true,
        },
      };
    }

    setRampSettings(settings);
    setOriginalSettings(settings);
  }, [deal]);

  useEffect(() => {
    if (deal && rampSettings) {
      const metrics = calculateAllRampMetrics(deal, rampSettings);
      setCalculatedMetrics(metrics);
    }
  }, [deal, rampSettings]);

  useEffect(() => {
    loadRampData();
  }, [loadRampData]);

  const animateFields = useCallback((fields: string[]) => {
    setAnimatedFields(new Set(fields));
    setTimeout(() => setAnimatedFields(new Set()), 1000);
  }, []);

  const debouncedRecalculate = useCallback(
    debounceNode(() => {
      animateFields(["summary", "preview"]);
    }, 200),
    [animateFields],
  );

  const handleRevenueRampSelect = (preset: RampPresetKey) => {
    if (!rampSettings) return;
    const newSettings = {
      ...rampSettings,
      revenueRamp: revenueRampPresets[preset],
    };
    setRampSettings(newSettings);
    debouncedRecalculate();
  };

  const handleCostRampSelect = (preset: RampPresetKey) => {
    if (!rampSettings) return;
    const newSettings = {
      ...rampSettings,
      costRamp: costRampPresets[preset],
    };
    setRampSettings(newSettings);
    debouncedRecalculate();
  };

  const handleCostRampToggle = (
    group: keyof RampSettings["applyCostRamp"],
    enabled: boolean,
  ) => {
    if (!rampSettings || group === "exclude") return;
    const newSettings = {
      ...rampSettings,
      applyCostRamp: {
        ...rampSettings.applyCostRamp,
        [group]: enabled,
      },
    };
    setRampSettings(newSettings);
    debouncedRecalculate();
  };

  const handleMacroChange = (field: keyof RampSettings, value: number) => {
    if (!rampSettings) return;
    const newSettings = {
      ...rampSettings,
      [field]: value,
    };
    setRampSettings(newSettings);
    debouncedRecalculate();
  };

  const handleSave = async () => {
    if (!deal || !rampSettings) return;
    setSaveState("saving");

    try {
      const updatedKpis = {
        ...(deal.kpis || {}),
        rampSettings: { ...rampSettings },
        ramp: {
          ...(deal.kpis?.ramp || {}),
          inflationRate: rampSettings.inflationPct,
          revenueGrowthY2: rampSettings.toplineGrowthPct,
          revenueGrowthY3Plus: rampSettings.toplineGrowthPct,
        },
      };

      const updatedDeal: Deal = {
        ...deal,
        kpis: updatedKpis,
        updatedAt: new Date().toISOString(),
      };

      onDealChange(updatedDeal);

      await saveProject({
        id: deal.id,
        name: deal.name,
        property_id: null,
        stage: "underwriting",
        currency: deal.currency,
        kpis: updatedKpis,
      });

      toast.success("Success to save settings");

      setCompleted(deal.id!, "rampSettings" as any, true);
      setOriginalSettings(rampSettings);
      setSaveState("success");
      if (onSaved) onSaved();
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (error) {
      console.error("[RampMacroSettings] Save failed:", error);
      setSaveState("idle");
      toast.error("Failed to save settings");
    }
  };
  const handleCancel = () => {
    if (originalSettings) {
      setRampSettings(originalSettings);
      toast.info("Changes discarded");
    }
  };

  if (!deal || !rampSettings || !calculatedMetrics) {
    return <div>Loading ramp settings...</div>;
  }

  const getAnimationClass = (field: string) => {
    return animatedFields.has(field)
      ? "animate-pulse bg-brand-50 transition-all duration-500"
      : "";
  };

  const currentRevenuePreset = getCurrentPreset(
    rampSettings.revenueRamp,
    revenueRampPresets,
  ) as RampPresetKey | "custom";

  const currentCostPreset = getCurrentPreset(
    rampSettings.costRamp,
    costRampPresets,
  ) as RampPresetKey | "custom";

  const depreciationCalculation = calculateAnnualDepreciation(
    deal,
    rampSettings,
  );
  const indexes = calculateIndexes(rampSettings);
  const settingsSummary = getSettingsSummary(rampSettings);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Ramp-up, Growth, Inflation & Other Settings
        </h3>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="text-blue-800 text-sm font-medium">
            Configure revenue and cost ramp-up curves for Years 1-4, plus
            long-term growth and inflation assumptions.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Revenue Ramp-up Scenarios
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              Revenue ramp applies to all topline streams (Rooms, F&B, Other)
              during the first 4 years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-fr">
            <RampCard
              title="Conservative"
              subtitle="Slower ramp to stabilization"
              series={[70, 80, 90, 100]}
              active={currentRevenuePreset === "conservative"}
              onSelect={() => handleRevenueRampSelect("conservative")}
              color="blue"
            />
            <RampCard
              title="Standard"
              subtitle="Balanced ramp-up approach"
              series={[80, 90, 100, 100]}
              active={currentRevenuePreset === "standard"}
              onSelect={() => handleRevenueRampSelect("standard")}
              color="blue"
            />
            <RampCard
              title="Ambitious"
              subtitle="Faster path to stabilization"
              series={[85, 100, 100, 100]}
              active={currentRevenuePreset === "ambitious"}
              onSelect={() => handleRevenueRampSelect("ambitious")}
              color="blue"
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Operating Cost Ramp-up Scenarios
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                Cost ramp applies to all operating costs by default.
              </p>
            </div>
            <div className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full border border-green-200 flex items-center">
              <svg
                className="w-3 h-3 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                ></path>
              </svg>
              All cost groups included
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-fr">
            <RampCard
              title="Conservative"
              subtitle="Higher initial cost premium"
              series={[115, 110, 100, 100]}
              active={currentCostPreset === "conservative"}
              onSelect={() => handleCostRampSelect("conservative")}
              color="blue"
              scale={{ min: 100, max: 120 }}
            />
            <RampCard
              title="Standard"
              subtitle="Moderate cost premium"
              series={[110, 105, 100, 100]}
              active={currentCostPreset === "standard"}
              onSelect={() => handleCostRampSelect("standard")}
              color="blue"
              scale={{ min: 100, max: 120 }}
            />
            <RampCard
              title="Ambitious"
              subtitle="Lower initial cost premium"
              series={[108, 102, 100, 100]}
              active={currentCostPreset === "ambitious"}
              onSelect={() => handleCostRampSelect("ambitious")}
              color="blue"
              scale={{ min: 100, max: 120 }}
            />
          </div>

          {/* Cost Group Toggles - Disabled */}
          <div className="mt-6 opacity-70">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-900">
                Apply Cost Ramp To:
              </h4>
              <span className="text-xs text-slate-500 italic">
                (All selected)
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LabelCheck
                checked={true}
                onChange={() => {}}
                title="Departmental Costs"
                hint="Rooms, F&B, Other direct costs (COGS, commissions)"
                disabled
              />
              <LabelCheck
                checked={true}
                onChange={() => {}}
                title="Other Operating Expenses"
                hint="Management fees, insurance, taxes"
                disabled
              />
              <LabelCheck
                checked={true}
                onChange={() => {}}
                title="Undistributed Costs"
                hint="A&G, S&M, Maintenance, Utilities, Tech"
                disabled
              />
              <LabelCheck
                checked={true}
                onChange={() => {}}
                title="Payroll Costs"
                hint="Apply premium to all payroll totals"
                disabled
              />
            </div>
            <div className="mt-3 text-xs text-slate-500 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
              <strong>Always excluded from ramp:</strong> Depreciation,
              Interest, CapEx, Rent
            </div>
          </div>
        </div>
      </div>
      {/* Macro Settings */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Topline Growth */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <h3 className="text-sm font-semibold text-slate-900">
                Topline Growth
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">
                      Applies from Year 1 onward to all revenue streams (Rooms,
                      F&B, Other). Year 1 = 100%, каждый следующий год
                      увеличивается на этот процент.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Annual Growth Rate
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    type="number"
                    value={rampSettings.toplineGrowthPct || ""}
                    onChange={(e) =>
                      handleMacroChange(
                        "toplineGrowthPct",
                        Number(e.target.value) || 0,
                      )
                    }
                    className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    min="0"
                    max="20"
                    step="0.1"
                  />
                  <span className="absolute right-3 top-2 text-slate-500 text-sm">
                    %
                  </span>
                </div>
                <div className="flex space-x-2">
                  {[2, 3, 4, 5, 6].map((pct) => (
                    <QuickButton
                      key={pct}
                      value={pct}
                      active={rampSettings.toplineGrowthPct === pct}
                      onClick={() => handleMacroChange("toplineGrowthPct", pct)}
                    />
                  ))}
                </div>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Applies to revenue streams from Year 1 onward
              </p>
            </div>

            {/* Topline Index Preview */}
            <div className="mt-4 bg-slate-50 rounded-lg p-3">
              <div className="text-sm text-slate-600 mb-2">
                Topline Index (10 years):
              </div>
              <div className="text-xs font-medium text-slate-900 grid grid-cols-5 gap-1">
                {indexes.toplineIndex.slice(0, 5).map((value, index) => (
                  <div key={index} className="text-center">
                    <div className="font-semibold">Y{index + 1}</div>
                    <div>{value.toFixed(1)}%</div>
                  </div>
                ))}
              </div>
              <div className="text-xs font-medium text-slate-900 grid grid-cols-5 gap-1 mt-2">
                {indexes.toplineIndex.slice(5, 10).map((value, index) => (
                  <div key={index + 5} className="text-center">
                    <div className="font-semibold">Y{index + 6}</div>
                    <div>{value.toFixed(1)}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Inflation */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Settings className="h-5 w-5 text-purple-600" />
              <h3 className="text-sm font-semibold text-slate-900">
                Inflation
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">
                      Applies to payroll and operating costs from Year 1 onward.
                      Year 1 = 100%, каждый следующий год увеличивается на этот
                      процент.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Annual Inflation Rate
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    type="number"
                    value={rampSettings.inflationPct || ""}
                    onChange={(e) =>
                      handleMacroChange(
                        "inflationPct",
                        Number(e.target.value) || 0,
                      )
                    }
                    className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    min="0"
                    max="15"
                    step="0.1"
                  />
                  <span className="absolute right-3 top-2 text-slate-500 text-sm">
                    %
                  </span>
                </div>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((pct) => (
                    <QuickButton
                      key={pct}
                      value={pct}
                      active={rampSettings.inflationPct === pct}
                      onClick={() => handleMacroChange("inflationPct", pct)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Inflation Index Preview */}
            <div className="mt-4 bg-slate-50 rounded-lg p-3">
              <div className="text-sm text-slate-600 mb-2">
                Inflation Index (10 years):
              </div>
              <div className="text-xs font-medium text-slate-900 grid grid-cols-5 gap-1">
                {indexes.inflationIndex.slice(0, 5).map((value, index) => (
                  <div key={index} className="text-center">
                    <div className="font-semibold">Y{index + 1}</div>
                    <div>{value.toFixed(1)}%</div>
                  </div>
                ))}
              </div>
              <div className="text-xs font-medium text-slate-900 grid grid-cols-5 gap-1 mt-2">
                {indexes.inflationIndex.slice(5, 10).map((value, index) => (
                  <div key={index + 5} className="text-center">
                    <div className="font-semibold">Y{index + 6}</div>
                    <div>{value.toFixed(1)}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Depreciation */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">Depreciation</h3>

          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-sm text-slate-600 mb-2">Calculation:</div>
            <div className="text-sm font-medium text-slate-900">
              Annual Depreciation = {rampSettings.depreciationPctOfCapex}% ×{" "}
              {formatCurrency(
                depreciationCalculation.capexAmount,
                deal.currency,
              )}{" "}
              =
              <span className="text-brand-600 font-semibold ml-1">
                {formatCurrency(
                  depreciationCalculation.annualDepreciation,
                  deal.currency,
                )}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Depreciation % of CapEx
            </label>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <input
                  type="number"
                  value={rampSettings.depreciationPctOfCapex || ""}
                  onChange={(e) =>
                    handleMacroChange(
                      "depreciationPctOfCapex",
                      Number(e.target.value) || 0,
                    )
                  }
                  className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  min="0"
                  max="10"
                  step="0.1"
                />
                <span className="absolute right-3 top-2 text-slate-500 text-sm">
                  %
                </span>
              </div>
              <span className="text-sm text-slate-600">
                of Grand Total excl. VAT from Investment Budget
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Summary */}
      <div
        className={`rounded-xl border border-slate-200 bg-white p-6 shadow-sm ${getAnimationClass("summary")}`}
      >
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Selected Summary
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">
                  Revenue Ramp (Y1-Y4)
                </div>
                <div className="flex space-x-2">
                  {calculatedMetrics.revenueRampMultipliers.map((val, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-primary-blue text-white rounded text-xs font-medium"
                    >
                      Y{i + 1}: {val}%
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">
                  Cost Ramp (Y1-Y4)
                </div>
                <div className="flex space-x-2">
                  {calculatedMetrics.costRampMultipliers.map((val, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium"
                    >
                      Y{i + 1}: {val}%
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Topline Growth:</span>
                <span className="font-medium text-slate-900">
                  {settingsSummary.toplineGrowth}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Inflation:</span>
                <span className="font-medium text-slate-900">
                  {settingsSummary.inflation}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Annual Depreciation:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(
                    calculatedMetrics.annualDepreciation,
                    deal.currency,
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {!hideActions && (
        <div className="flex items-center space-x-3 pt-4 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={
              saveState === "saving" ||
              !hasChanges(originalSettings, rampSettings)
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              saveState === "saving" ||
              !hasChanges(originalSettings, rampSettings)
            }
            className={`${
              saveState === "success"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_40%)]"
            } text-white flex items-center space-x-2`}
          >
            {saveState === "saving" && (
              <TrendingUp className="h-4 w-4 animate-spin" />
            )}
            {saveState === "success" && <TrendingUp className="h-4 w-4" />}
            <span>
              {saveState === "saving" && "Saving..."}
              {saveState === "success" && "Saved ✔"}
              {saveState === "idle" && "Save Settings"}
            </span>
          </Button>
        </div>
      )}

      {/* Footer timestamp */}
      <div className="text-center">
        <p className="text-xs text-slate-500">
          Recalculated • {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
